/**
 * The Action
 * @typedef {{name:string}} ExtensionAction
 */

/**
 * The Sender
 * @typedef {{internalId?: string}} ExtensionSender
 */

import { isNullish } from "./typeHelper.js";

/**
 * The Extension Handler
 * @typedef {{sender: ExtensionSender, action: ExtensionAction, forceEarly: boolean}} ExtensionHandler
 */

export default class EventManager {
	constructor() {
		/**
		 * Stores handlers for events
		 * @type {Map<string, ExtensionHandler[]>}
		 */
		this.extensions = new Map();
	}

	/**
	 *
	 * @param sender
	 * @param {string} eventName
	 * @param {ExtensionAction} handler
	 * @param {boolean} forceEarly
	 */
	on(sender, eventName, handler, forceEarly) {
		if (!this.extensions.has(eventName)) {
			this.extensions.set(eventName, []);
		}

		const handlerData = {
			sender: sender,
			action: handler,
			forceEarly,
		};

		if (!this.containsHandler(handlerData, eventName)) {
			this.extensions.get(eventName).push(handlerData);
		}
	}

	/**
	 *
	 * @param {ExtensionHandler} handler
	 * @param {string} eventName
	 * @returns {boolean}
	 */
	containsHandler(handler, eventName) {
		const handlers = this.extensions.get(eventName);

		if (isNullish(handlers)) {
			return false;
		}

		const matchingHandlers = handlers.filter((extension) => {
			return this.equals(extension, handler);
		});

		return matchingHandlers.length > 0;
	}

	/**
	 *
	 * @param {ExtensionHandler} extension
	 * @param {ExtensionHandler} handler
	 * @returns {boolean}
	 */
	equals(extension, handler) {
		return extension.action.name === handler.action.name && this.idOf(extension.sender) === this.idOf(handler.sender);
	}

	idOf(sender) {
		if (!sender.internalId) {
			sender.internalId = Date.now().toString(36) + Math.random().toString(36).substring(2);
		}

		return sender.internalId;
	}

	/**
	 *
	 * @param {string} eventName
	 * @param handler
	 */
	off(eventName, handler) {
		if (!this.extensions) {
			return;
		}

		const handlers = this.extensions.get(eventName);

		if (isNullish(handlers)) {
			return;
		}

		for (let i = 0; i < handlers.length; i++) {
			if (handlers[i] === handler) {
				handlers.splice(i--, 1);
			}
		}
	}

	/**
	 *
	 * @param {string} eventName
	 * @param args
	 */
	trigger(eventName, ...args) {
		if (!this.extensions.has(eventName)) {
			return;
		}

		this.executeEarlyListeners(eventName, args);
		this.executeLateListeners(eventName, args);
	}

	/**
	 *
	 * @param {string} eventName
	 * @param args
	 */
	executeEarlyListeners(eventName, ...args) {
		const listeners = this.extensions.get(eventName) ?? [];

		listeners
			.filter((extension) => {
				return extension.forceEarly;
			})
			.forEach((handler) => handler.action.apply(this, ...args));
	}

	/**
	 *
	 * @param {string} eventName
	 * @param args
	 */
	executeLateListeners(eventName, ...args) {
		const listeners = this.extensions.get(eventName) ?? [];
		listeners
			.filter((extension) => {
				return !extension.forceEarly;
			})
			.forEach((handler) => handler.action.apply(this, ...args));
	}
}
